import {Button, Group, Paper, SimpleGrid, Stack, Text, Title} from '@mantine/core'
import {DateInput} from '@mantine/dates'
import {IconCalendar, IconClock} from '@tabler/icons-react'
import {useState} from 'react'

import {
    isScheduleLockedServiceJob,
    SCHEDULE_LOCKED_JOB_HINT,
} from '../service-schedules/preview-labels'
import {showDateMoveWarnings} from './date-move-warnings'
import {useEditRights} from './edit-rights-context'
import type {JobDataLayer} from './job-data-layer'
import {
    formatDateTbcLabel,
    formatJobScheduleRange,
    parseApiDate,
    toLocalDate,
} from './job-schedule-format'
import {validateJobScheduleInput} from './job-schedule-validation'
import {JobTimePicker} from './job-time-picker'
import type {JobDetail, JobScheduleInput} from './types'

type JobScheduleSectionProps = {
    job: JobDetail
    jobDataLayer: JobDataLayer
    onJobUpdated: (job: JobDetail) => void
}

function jobToScheduleInput(job: JobDetail): JobScheduleInput {
    return {
        startDate: parseApiDate(job.startDate),
        startTime: job.startTime,
        endDate: job.endDate ? parseApiDate(job.endDate) : null,
        endTime: job.endTime,
    }
}

/** Mantine's DateInput yields an ISO `YYYY-MM-DD` string; normalize it to a local Date. */
function parseDateInputValue(value: string | Date | null): Date | null {
    return value == null ? null : toLocalDate(value)
}

export function JobScheduleSection({job, jobDataLayer, onJobUpdated}: JobScheduleSectionProps) {
    const {canEdit} = useEditRights()
    const [isEditing, setIsEditing] = useState(false)
    const [saving, setSaving] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
    const [values, setValues] = useState<JobScheduleInput>(() => jobToScheduleInput(job))

    function startEditing() {
        setValues(jobToScheduleInput(job))
        setFieldErrors({})
        setErrorMessage(null)
        setIsEditing(true)
    }

    function handleCancel() {
        setIsEditing(false)
        setFieldErrors({})
        setErrorMessage(null)
    }

    async function handleSave() {
        const scheduleValues = {...values, endTime: null}
        const validationErrors = validateJobScheduleInput({
            startDate: scheduleValues.startDate,
            endDate: scheduleValues.endDate,
            startTime: scheduleValues.startTime,
            endTime: scheduleValues.endTime,
        })
        if (Object.keys(validationErrors).length > 0) {
            setFieldErrors(validationErrors)
            return
        }

        setSaving(true)
        setErrorMessage(null)
        setFieldErrors({})
        try {
            const updatedJob = await jobDataLayer.saveSchedule(job.id, scheduleValues)
            onJobUpdated(updatedJob)
            setIsEditing(false)
            showDateMoveWarnings(updatedJob.warnings)
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to save schedule')
        } finally {
            setSaving(false)
        }
    }

    const scheduleLabel = job.dateTbc
        ? formatDateTbcLabel(job.startTime)
        : formatJobScheduleRange({
              startDate: job.startDate,
              endDate: job.endDate,
              startTime: job.startTime,
              endTime: null,
          })
    const scheduleLocked = isScheduleLockedServiceJob(job.scheduleRole)

    return (
        <Paper withBorder p="md" radius="md">
            <Stack gap="md">
                <Group justify="space-between" align="center">
                    <Title order={3}>Schedule</Title>
                    {!isEditing ? (
                        scheduleLocked || !canEdit ? null : (
                            <Button variant="subtle" size="compact-sm" onClick={startEditing}>
                                Edit
                            </Button>
                        )
                    ) : (
                        <Group gap="sm">
                            <Button
                                variant="default"
                                size="compact-sm"
                                onClick={handleCancel}
                                disabled={saving}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="compact-sm"
                                onClick={() => void handleSave()}
                                loading={saving}
                            >
                                Save
                            </Button>
                        </Group>
                    )}
                </Group>

                {errorMessage && (
                    <Text size="sm" c="red">
                        {errorMessage}
                    </Text>
                )}

                {isEditing ? (
                    <Stack gap="md">
                        <SimpleGrid cols={2}>
                            <DateInput
                                label="Start date"
                                required
                                leftSection={<IconCalendar size={16} />}
                                valueFormat="MMM D, YYYY"
                                value={values.startDate}
                                onChange={(value) =>
                                    setValues((current) => ({
                                        ...current,
                                        startDate: parseDateInputValue(value) ?? current.startDate,
                                    }))
                                }
                                error={fieldErrors['startDate']}
                            />
                            <DateInput
                                label="End date (optional)"
                                leftSection={<IconCalendar size={16} />}
                                valueFormat="MMM D, YYYY"
                                value={values.endDate}
                                onChange={(value) => {
                                    const parsed = parseDateInputValue(value)
                                    setValues((current) => ({
                                        ...current,
                                        endDate: parsed,
                                        endTime: parsed == null ? null : current.endTime,
                                    }))
                                }}
                                error={fieldErrors['endDate']}
                            />
                        </SimpleGrid>
                        <SimpleGrid cols={2}>
                            <JobTimePicker
                                label="Start time (optional)"
                                description="Driver arrival time"
                                leftSection={<IconClock size={16} />}
                                value={values.startTime}
                                onChange={(startTime) =>
                                    setValues((current) => ({...current, startTime}))
                                }
                                error={fieldErrors['startTime']}
                            />
                        </SimpleGrid>
                    </Stack>
                ) : (
                    <Stack gap="xs">
                        <Text size="sm">Schedule: {scheduleLabel}</Text>
                        {scheduleLocked && (
                            <Text size="sm" c="dimmed">
                                {SCHEDULE_LOCKED_JOB_HINT}
                            </Text>
                        )}
                    </Stack>
                )}
            </Stack>
        </Paper>
    )
}
